import { useState, useEffect } from 'react';
import { getDepartments, createDepartment, updateDepartment, deleteDepartment } from '../api/departments';
import { getQueues, createQueue, updateQueue, deleteQueue } from '../api/queues';
import { useToast } from '../context/ToastContext';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Topbar } from '../layout/Topbar';
import type { Department, Queue } from '../types';

export function DepartmentsPage() {
  const { addToast } = useToast();
  const [departments, setDepartments] = useState<Department[]>([]);
  const [queues, setQueues] = useState<Queue[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'departments' | 'queues'>('departments');

  // Department form
  const [deptSelected, setDeptSelected] = useState<Department | null>(null);
  const [deptName, setDeptName] = useState('');
  const [deptType, setDeptType] = useState('');

  // Queue form
  const [queueSelected, setQueueSelected] = useState<Queue | null>(null);
  const [queueName, setQueueName] = useState('');
  const [queueDeptId, setQueueDeptId] = useState('');
  const [queueActive, setQueueActive] = useState(true);

  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ type: 'dept' | 'queue'; id: string } | null>(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [d, q] = await Promise.all([getDepartments(), getQueues()]);
      setDepartments(d);
      setQueues(q);
    } catch {
      addToast('error', 'Falha ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // ── Department CRUD ──
  const deptNew = () => {
    setDeptSelected(null); setDeptName(''); setDeptType('');
  };
  const deptEdit = (d: Department) => {
    setDeptSelected(d); setDeptName(d.name); setDeptType(d.type);
  };
  const deptSave = async () => {
    if (!deptName.trim() || !deptType) return;
    setSaving(true);
    try {
      if (deptSelected) {
        await updateDepartment(deptSelected.id, { name: deptName, type: deptType });
        addToast('success', 'Departamento atualizado');
      } else {
        await createDepartment({ name: deptName, type: deptType });
        addToast('success', 'Departamento criado');
      }
      deptNew();
      loadData();
    } catch {
      addToast('error', 'Falha ao salvar departamento');
    } finally {
      setSaving(false);
    }
  };
  const deptDelete = async (id: string) => {
    try {
      await deleteDepartment(id);
      if (deptSelected?.id === id) deptNew();
      addToast('success', 'Departamento excluído');
      loadData();
    } catch {
      addToast('error', 'Falha ao excluir');
    } finally {
      setConfirmDelete(null);
    }
  };

  // ── Queue CRUD ──
  const queueNew = () => {
    setQueueSelected(null); setQueueName(''); setQueueDeptId(departments[0]?.id || ''); setQueueActive(true);
  };
  const queueEdit = (q: Queue) => {
    setQueueSelected(q); setQueueName(q.name); setQueueDeptId(q.departmentId); setQueueActive(q.isActive);
  };
  const queueSave = async () => {
    if (!queueName.trim() || !queueDeptId) return;
    setSaving(true);
    try {
      if (queueSelected) {
        await updateQueue(queueSelected.id, { name: queueName, isActive: queueActive });
        addToast('success', 'Fila atualizada');
      } else {
        await createQueue({ name: queueName, departmentId: queueDeptId });
        addToast('success', 'Fila criada');
      }
      queueNew();
      loadData();
    } catch {
      addToast('error', 'Falha ao salvar fila');
    } finally {
      setSaving(false);
    }
  };
  const queueDelete = async (id: string) => {
    try {
      await deleteQueue(id);
      if (queueSelected?.id === id) queueNew();
      addToast('success', 'Fila excluída');
      loadData();
    } catch {
      addToast('error', 'Falha ao excluir');
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="view-container">
      <Topbar title="Departamentos & Filas" subtitle="Organização da equipe e fluxo de atendimento." />

      <div className="flex gap-sm" style={{ marginBottom: 16 }}>
        <button className={tab === 'departments' ? '' : 'secondary'} onClick={() => setTab('departments')}>🏢 Departamentos</button>
        <button className={tab === 'queues' ? '' : 'secondary'} onClick={() => setTab('queues')}>📋 Filas</button>
      </div>

      {tab === 'departments' && (
        <div className="records-layout">
          <div className="panel">
            <div className="panel-header">
              <h2>Departamentos ({departments.length})</h2>
              <button onClick={deptNew}>+ Novo</button>
            </div>
            {loading ? <div className="empty-state"><div className="spinner" /></div>
            : departments.length === 0 ? <div className="empty-state"><p>Nenhum departamento.</p></div>
            : <div className="data-list">
                {departments.map((d) => (
                  <div key={d.id} className={`data-list-item${deptSelected?.id === d.id ? ' active' : ''}`}
                    onClick={() => deptEdit(d)}>
                    <div><div className="item-name">{d.name}</div></div>
                    <span className="pill neutral">{d.type}</span>
                  </div>
                ))}
              </div>
            }
          </div>
          <div className="panel">
            <div className="panel-header"><h2>{deptSelected ? 'Editar' : 'Novo'} departamento</h2></div>
            <div className="panel-body">
              <div className="form-stack">
                <label>Nome <input value={deptName} onChange={(e) => setDeptName(e.target.value)} placeholder="Financeiro" /></label>
                <label>Tipo
                  <select value={deptType} onChange={(e) => setDeptType(e.target.value)}>
                    <option value="">Selecione</option>
                    <option value="FINANCEIRO">Financeiro</option>
                    <option value="SUPORTE_TECNICO">Suporte Técnico</option>
                    <option value="VENDAS">Vendas</option>
                    <option value="ADMINISTRATIVO">Administrativo</option>
                  </select>
                </label>
                <div className="flex gap-sm">
                  <button onClick={deptSave} disabled={saving}>{saving ? '...' : 'Salvar'}</button>
                  <button className="secondary" onClick={deptNew}>Cancelar</button>
                  {deptSelected && <button className="danger" onClick={() => setConfirmDelete({ type: 'dept', id: deptSelected.id })}>Excluir</button>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'queues' && (
        <div className="records-layout">
          <div className="panel">
            <div className="panel-header">
              <h2>Filas ({queues.length})</h2>
              <button onClick={queueNew}>+ Nova</button>
            </div>
            {loading ? <div className="empty-state"><div className="spinner" /></div>
            : queues.length === 0 ? <div className="empty-state"><p>Nenhuma fila.</p></div>
            : <div className="data-list">
                {queues.map((q) => (
                  <div key={q.id} className={`data-list-item${queueSelected?.id === q.id ? ' active' : ''}`}
                    onClick={() => queueEdit(q)}>
                    <div>
                      <div className="item-name">{q.name}</div>
                      <div className="item-detail">{q.department?.name || 'Sem departamento'}</div>
                    </div>
                    <span className={`pill ${q.isActive ? 'success' : 'neutral'}`}>{q.isActive ? 'Ativa' : 'Inativa'}</span>
                  </div>
                ))}
              </div>
            }
          </div>
          <div className="panel">
            <div className="panel-header"><h2>{queueSelected ? 'Editar' : 'Nova'} fila</h2></div>
            <div className="panel-body">
              <div className="form-stack">
                <label>Nome <input value={queueName} onChange={(e) => setQueueName(e.target.value)} placeholder="Fila Financeiro" /></label>
                <label>Departamento
                  <select value={queueDeptId} onChange={(e) => setQueueDeptId(e.target.value)}>
                    {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </label>
                <label className="checkbox-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={queueActive} onChange={(e) => setQueueActive(e.target.checked)} />
                  Fila ativa
                </label>
                <div className="flex gap-sm">
                  <button onClick={queueSave} disabled={saving}>{saving ? '...' : 'Salvar'}</button>
                  <button className="secondary" onClick={queueNew}>Cancelar</button>
                  {queueSelected && <button className="danger" onClick={() => setConfirmDelete({ type: 'queue', id: queueSelected.id })}>Excluir</button>}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDelete}
        title="Confirmar exclusão"
        message="Tem certeza? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        onConfirm={() => {
          if (!confirmDelete) return;
          if (confirmDelete.type === 'dept') deptDelete(confirmDelete.id);
          else queueDelete(confirmDelete.id);
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
